import type { CSSProperties } from "react";

import {
  LOCKUP_WORDMARK,
  MARK_RIBS,
  MARK_SHEET,
  type Tone,
  TONES,
  WORDMARK_BOUNDS,
  WORDMARK_PATH,
} from "./geometry";

const [WORDMARK_LEFT, , WORDMARK_WIDTH] = WORDMARK_BOUNDS;

// The symbol's 48 box mapped into the wordmark's units, so the wordmark can be
// its own <svg> beside the symbol and still sit exactly where the lockup puts it.
const LOCKUP_WORDMARK_VIEWBOX = [
  WORDMARK_LEFT,
  -LOCKUP_WORDMARK.baseline / LOCKUP_WORDMARK.scale,
  WORDMARK_WIDTH,
  48 / LOCKUP_WORDMARK.scale,
].join(" ");

// Gap and wordmark width as fractions of the symbol's box.
const LOCKUP_GAP =
  (LOCKUP_WORDMARK.x + WORDMARK_LEFT * LOCKUP_WORDMARK.scale - 48) / 48;
const LOCKUP_WORDMARK_WIDTH = (WORDMARK_WIDTH * LOCKUP_WORDMARK.scale) / 48;

const BLOCK: CSSProperties = { display: "block", flex: "none" };

/** The symbol, sized by its whole 48 box: the empty edges are its clear space. */
export function TinsmithMark({
  size = 24,
  tone = "light",
}: {
  readonly size?: number;
  readonly tone?: Tone;
}) {
  const { ribs, sheet } = TONES[tone];
  return (
    <svg
      aria-hidden="true"
      height={size}
      style={BLOCK}
      viewBox="0 0 48 48"
      width={size}
    >
      <polygon fill={sheet} points={MARK_SHEET} />
      {MARK_RIBS.map((points) => (
        <polygon fill={ribs} key={points} points={points} />
      ))}
    </svg>
  );
}

/**
 * Symbol and wordmark, `size` being the symbol's box. The wordmark is outlined,
 * so no font loads; `wordmarkClassName` lets a caller hide it on narrow
 * screens, leaving the symbol.
 */
export function TinsmithLockup({
  size = 24,
  tone = "light",
  wordmarkClassName,
}: {
  readonly size?: number;
  readonly tone?: Tone;
  readonly wordmarkClassName?: string;
}) {
  return (
    <span
      aria-label="Tinsmith"
      role="img"
      style={{
        alignItems: "center",
        display: "inline-flex",
        gap: LOCKUP_GAP * size,
      }}
    >
      <TinsmithMark size={size} tone={tone} />
      <svg
        aria-hidden="true"
        className={wordmarkClassName}
        height={size}
        // No inline `display`, so `wordmarkClassName` can hide it.
        style={{ flex: "none" }}
        viewBox={LOCKUP_WORDMARK_VIEWBOX}
        width={LOCKUP_WORDMARK_WIDTH * size}
      >
        <path d={WORDMARK_PATH} fill={TONES[tone].word} />
      </svg>
    </span>
  );
}
